import { getBackWeightFields, BackWeightFields, emptyBackWeight } from '@/library/BackWeightHelper';
import { useState, useEffect, useMemo } from 'react';
import EditableTile from '@/common/EditableTile';
import EditableDropdown, { IDropdownOption } from '@/common/EditableDropdown';
import Toggle from '@/common/Toggle';
import RadioGroup from '@/common/RadioGroup';
import Tile from '@/common/Tile';
import { IIronStaticDropdownData, IIronDynamicDropdownData } from './useEditIron';
import { HandHelper, LoftLieHelper, ShaftLengthHelper, SwingWeightHelper, ShaftTippingHelper } from '@/library';
import { IShaftOptionMap } from '@/library/ShaftHelper';
import { TourBagIronDetail } from '@/api/models';
import { normalizeString } from '@/utils';
import { IIronClub } from './IronList';
import clubStyles from '../Club.module.scss';
// Reuse Iron components
import FinalHeadWeightModal, { IFinalHeadWeightIron } from '../Iron/FinalHeadWeightModal';
import { calculateFinalHeadWeight } from '../Iron/caluclateFinalHeadWeight';
import { handAbbreviationToFullName } from '@/library/HandHelper';

interface IEditIronProps {
  title: 'Create Club' | 'Edit Club';
  iron: TourBagIronDetail;
  handDropdownData: HandHelper.IHandOptionMap | undefined;
  loftDropdownData: LoftLieHelper.ILoftOptionMap | undefined;
  lieDropdownData: LoftLieHelper.ILieOptionMap | undefined;
  ironStaticDropdownData: IIronStaticDropdownData | undefined;
  ironDynamicDropdownData: IIronDynamicDropdownData | undefined;
  shaftDropdownData: IShaftOptionMap | undefined;
  shaftLengthDropdownData: ShaftLengthHelper.ShaftLengthOptionMap | undefined;
  swingWtDropdownData: SwingWeightHelper.ISwingWtOptionMap | undefined;
  roundRibDropdownData: IDropdownOption[];
  logoDropdownData: IDropdownOption[];
  shaftTippingData: ShaftTippingHelper.ShaftTippingOptionMap | undefined;
  // eslint-disable-next-line no-unused-vars
  handleSave: (ironClub: IIronClub) => Promise<void>;
  handleCancel: () => void;
}

function EditIron({
  title,
  iron,
  handDropdownData,
  loftDropdownData,
  lieDropdownData,
  ironStaticDropdownData,
  ironDynamicDropdownData,
  shaftDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
  roundRibDropdownData,
  logoDropdownData,
  shaftTippingData,
  handleSave,
  handleCancel,
}: IEditIronProps) {
  const [showError, setShowError] = useState(false);

  // dropdown state
  const [ironHeadModelOptions, setIronHeadModelOptions] = useState<IDropdownOption[]>([]);

  // field state
  const [isActive, setIsActive] = useState(iron.isActive);
  const [ironHand, setIronHand] = useState(handAbbreviationToFullName(iron.hand));
  const [ironFamilyId, setIronFamilyId] = useState(iron.familyId || '');
  const [ironHeadModelId, setIronHeadModelId] = useState(iron.headModelId || '');
  const [ironClubTypeId, setIronClubTypeId] = useState(iron.ironClubId || '');
  const [loft, setLoft] = useState(`${iron.loftDigit}${iron.loftFraction}`);
  const [lie, setLie] = useState(`${iron.lieDigit}${iron.lieFraction}`);
  const [swingWt, setSwingWt] = useState(`${iron.swingWtPrefixName}${iron.swingWtSuffixName}`);
  const initialIronShaftId = useMemo(() => shaftDropdownData?.options.find((ironShaft) => ironShaft.label.endsWith(iron.shaftName))?.id ?? '', [iron.shaftName]);
  const [ironShaftId, setIronShaftId] = useState<string | null>(iron.shaftName ? initialIronShaftId : '');
  const [measured, setMeasured] = useState(iron.isShaftLengthFinished ? true : false);
  const [ironShaftLength, setIronShaftLength] = useState(`${iron.shaftLengthDigit} ${iron.shaftLengthFraction}`);
  const [ironShaftCosmetic, setIronShaftCosmetic] = useState(iron.shaftCosmetic);
  const [ironGripType, setIronGripType] = useState(iron.gripType || '');
  const [ironGripSizeId, setIronGripSizeId] = useState(iron.gripSizeId || '');
  const [ironRoundRib, setIronRoundRib] = useState<string>(iron.rndRib);
  const [ironLogo, setIronLogo] = useState<string>(iron.logo);
  const [ironGripTape, setIronGripTape] = useState(iron.tape);
  const [notes, setNotes] = useState(iron.notes);
  const [showFinalHWModal, setShowFinalHWModal] = useState(false);
  const [backWeight, setBackWeight] = useState<BackWeightFields>(() => getBackWeightFields(iron));
  const [headWeight, setHeadWeight] = useState(iron.headWeight);
  const [drill, setDrill] = useState(iron.drill);
  const [tipPlug, setTipPlug] = useState(iron.tipPlug);
  const [glueWeight, setGlueWeight] = useState(iron.glueWeight);
  const [leadTape, setLeadTape] = useState(iron.leadTape);
  const [tipping, setTipping] = useState(`${iron.shaftTippingDigit} ${iron.shaftTippingFraction}`);
  const finalHeadWeight = calculateFinalHeadWeight({ headWeight, drill, tipPlug, glueWeight, leadTape });
  const finalHWIron: TourBagIronDetail = { ...iron, ...backWeight, headModelId: Number(ironHeadModelId) || undefined, headWeight, drill, tipPlug, glueWeight, leadTape };

  const handleUpdateFinalHW = (partialIron: IFinalHeadWeightIron) => {
    // update local state
    setHeadWeight(partialIron.headWeight);
    setDrill(partialIron.drill);
    setTipPlug(partialIron.tipPlug);
    setGlueWeight(partialIron.glueWeight);
    setLeadTape(partialIron.leadTape);
    setBackWeight(getBackWeightFields(partialIron));
  };

  const cancelEdition = () => {
    // TODO: We can detect if its creation/ edition mode and reset or not these values
    // reset values
    setIsActive(iron.isActive);
    setIronHand(handAbbreviationToFullName(iron.hand));
    setIronHeadModelId('');
    setLoft('');
    setLie('');
    setSwingWt('');
    setIronShaftId('');
    setMeasured(iron.isShaftLengthFinished ? true : false);
    setIronShaftLength('');
    setIronGripTape('');
    setNotes('');
    setHeadWeight(null);
    setDrill(null);
    setTipPlug(null);
    setGlueWeight(null);
    setLeadTape(null);
    // exit
    handleCancel();
  };

  const saveIron = async () => {
    const validIronHeadModel = ironHeadModelOptions.some(({ id }) => id === ironHeadModelId);

    if (!validIronHeadModel) {
      setShowError(true);
      return;
    }

    const ironClub: IIronClub = {
      ...backWeight,
      isActive,
      ironHand,
      ironFamilyId: Number(ironFamilyId),
      ironHeadModelId: Number(ironHeadModelId),
      ironClubTypeId: Number(ironClubTypeId),
      loft: loft,
      lie: lie,
      swingWt,
      measured,
      ironShaftId: Number(ironShaftId),
      ironShaftLength,
      ironShaftCosmetic,
      ironGripType,
      ironGripSizeId: Number(ironGripSizeId),
      ironRoundRib: ironRoundRib as 'Round' | 'Rib', // safe casting static dropdown
      ironLogo: ironLogo as 'Up' | 'Down', // safe casting static dropdown
      ironGripTape,
      notes,
      headWeight,
      drill,
      tipPlug,
      glueWeight,
      leadTape,
      tipping,
    };

    // pass state and call handleSave
    await handleSave(ironClub);
  };

  useEffect(() => {
    if (ironDynamicDropdownData?.ironHeadModel) {
      setIronHeadModelOptions(ironDynamicDropdownData.ironHeadModel.getOptions({ familyId: ironFamilyId }));
    }
  }, [ironDynamicDropdownData, ironFamilyId]);

  if (
    !handDropdownData ||
    !loftDropdownData ||
    !lieDropdownData ||
    !ironStaticDropdownData ||
    !ironDynamicDropdownData ||
    !shaftDropdownData ||
    !shaftLengthDropdownData ||
    !swingWtDropdownData ||
    !shaftTippingData
  )
    return null;

  return (
    <>
      <div className={`${clubStyles.tileRow} ${clubStyles.title}`}>{title}</div>
      <div className={clubStyles.tileRow}>
        <EditableDropdown label="Hand" initialId={ironHand} onChange={(hand) => setIronHand(hand)} options={handDropdownData.options} />
        <EditableDropdown
          label="Family"
          initialId={ironFamilyId}
          onChange={(familyId) => {
            setIronFamilyId(Number(familyId));
            setIronHeadModelId('');
            setBackWeight({ ...emptyBackWeight });
          }}
          options={ironStaticDropdownData.ironFamily.options}
          variant="large"
        />
        <EditableDropdown
          label="Model"
          initialId={ironHeadModelId}
          onChange={(headModelId) => {
            setIronHeadModelId(headModelId);
            setBackWeight({ ...emptyBackWeight });
          }}
          options={ironHeadModelOptions}
          variant="xxl"
        />
        <EditableDropdown label="Iron Club" initialId={ironClubTypeId} onChange={(clubTypeId) => setIronClubTypeId(clubTypeId)} options={ironStaticDropdownData.ironClub.options} />
        <Toggle isActive={isActive} label={isActive ? 'In the Bag' : 'Out of Bag'} onChange={() => setIsActive((prevState) => !prevState)} />
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableDropdown label="Loft" initialId={loft} onChange={(loft) => setLoft(loft)} options={loftDropdownData.options} />
        <EditableDropdown label="Lie" initialId={lie} onChange={(lie) => setLie(lie)} options={lieDropdownData.options} />
        <EditableDropdown label="Swing Wt." initialId={swingWt} onChange={(swingWt) => setSwingWt(swingWt)} options={swingWtDropdownData.options} />
        <span className={clubStyles.finalHWWrapper} role="button" onClick={() => setShowFinalHWModal(true)}>
          <Tile label="Final HW" value={normalizeString(finalHeadWeight?.toString())} tileStyle={clubStyles.finalHW} />
        </span>
        {/* <Tile label="Final HW" value="N/A" /> */}
      </div>
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Shaft</div>
      </div>
      <div className={clubStyles.tileRow}>
        <EditableDropdown
          label="Shaft"
          initialId={ironShaftId || ''}
          onChange={(shaftId) => setIronShaftId(shaftId)}
          options={shaftDropdownData.options}
          type="rectangle"
          alignment="left"
          variant="xxxxl"
        />
        {/* setting !measured to render the second options since its reversed in the designs */}
        <div className={clubStyles.radioGroup}>
          <RadioGroup selectedOption={!measured} firstLabel="Cut" secondLabel="Finished" onChange={() => setMeasured((prevState) => !prevState)} />
        </div>
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableDropdown label="Length" initialId={ironShaftLength} onChange={(shaftLength) => setIronShaftLength(shaftLength)} options={shaftLengthDropdownData.options} />
        <EditableTile label="Cosmetic" value={ironShaftCosmetic} onChange={(shaftCosmetic) => setIronShaftCosmetic(shaftCosmetic)} />
        <EditableDropdown label="Tipping" initialId={tipping} onChange={(newTipping) => setTipping(newTipping)} options={shaftTippingData.options} />
      </div>
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Grip</div>
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableTile value={ironGripType} onChange={(gripType) => setIronGripType(gripType)} alignment="left" variant="xxl" />
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableDropdown label="Grip Size" initialId={ironGripSizeId} onChange={(gripSizeId) => setIronGripSizeId(gripSizeId)} options={ironStaticDropdownData.gripSize.options} />
        <EditableDropdown label="Round/Rib" initialId={ironRoundRib} options={roundRibDropdownData} onChange={(roundRib) => setIronRoundRib(roundRib)} />
        <EditableDropdown label="Logo" initialId={ironLogo} options={logoDropdownData} onChange={(logo) => setIronLogo(logo)} />
        <EditableTile label="Grip Tape" value={ironGripTape} onChange={(gripTape) => setIronGripTape(gripTape)} variant="large" />
      </div>
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Notes</div>
      </div>
      <div className={clubStyles.tileRow}>
        <textarea className={clubStyles.editNotes} value={notes} name="notes" onChange={(event) => setNotes(event.target.value)} rows={5} />
      </div>
      {showError && <div className={clubStyles.errors}>Please select a family and a head model for the iron club in order to create it.</div>}
      <div className={clubStyles.buttonRow}>
        <button className={clubStyles.saveButton} onClick={saveIron}>
          Save
        </button>
        <button className={clubStyles.cancelButton} onClick={cancelEdition}>
          Cancel
        </button>
      </div>
      {showFinalHWModal && (
        <FinalHeadWeightModal
          showFinalHWModal={showFinalHWModal}
          closeFinalHWModal={() => setShowFinalHWModal(false)}
          iron={finalHWIron}
          backWeightData={ironStaticDropdownData.backWeight}
          handleUpdate={handleUpdateFinalHW}
          isEditMode={true}
        />
      )}
    </>
  );
}

export default EditIron;
